import { useState } from "react";
import { Check, ImageIcon } from "lucide-react";
import { productImage } from "@/lib/api";
import { fmtIDR } from "@/lib/constants";
import { LocationBadge, QtyProgress, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import ImageLightbox from "@/components/ImageLightbox";
import { LocationPicker } from "@/components/LocationPicker";

export default function ProductRow({ product, selectable, selected, onToggleSelect, onOpen, onPurchase, canPurchase, canChangeLocation, region, onLocationSaved }) {
  const img = productImage(product);
  const unassigned = !product.buying_location;
  const [zoom, setZoom] = useState(false);

  const handleClick = () => {
    if (selectable) onToggleSelect(product.id);
    else onOpen(product);
  };

  return (
    <div
      data-testid={`product-row-${product.id}`}
      onClick={handleClick}
      className={`flex cursor-pointer items-center gap-3 rounded-md border bg-card p-4 transition-transform hover:-translate-y-[1px] active:scale-[0.99] ${
        unassigned ? "border-l-4 border-l-red-500" : ""
      } ${selected ? "ring-2 ring-primary" : ""}`}
    >
      {selectable && (
        <span
          data-testid={`select-checkbox-${product.id}`}
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
            selected ? "border-primary bg-primary text-white" : "border-stone-300 bg-white"
          }`}
        >
          {selected && <Check className="h-4 w-4" />}
        </span>
      )}
      {img ? (
        <button
          type="button"
          data-testid={`product-photo-${product.id}`}
          aria-label={`View photo of ${product.name}`}
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            setZoom(true);
          }}
        >
          <img src={img} alt={product.name} className="h-16 w-16 rounded-md border bg-stone-100 object-cover" />
        </button>
      ) : (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md border bg-stone-100">
          <ImageIcon className="h-6 w-6 text-stone-300" />
        </div>
      )}
      <div className="min-w-0 flex-1 space-y-1.5">
        <p className="truncate font-heading text-base font-bold tracking-tight" data-testid={`product-name-${product.id}`}>
          {product.name}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {canChangeLocation && !selectable ? (
            <LocationPicker product={product} region={region} onSaved={onLocationSaved} />
          ) : (
            <LocationBadge location={product.buying_location} />
          )}
          <StatusBadge status={product.status} />
        </div>
        <QtyProgress ordered={product.ordered_qty} purchased={product.purchased_qty} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        {product.selling_price != null && (
          <span className="text-sm font-semibold text-stone-700">{fmtIDR(product.selling_price)}</span>
        )}
        {canPurchase && !selectable && (
          <Button
            data-testid={`record-purchase-btn-${product.id}`}
            size="sm"
            className="h-10 px-4"
            onClick={(e) => {
              e.stopPropagation();
              onPurchase(product);
            }}
          >
            Record
          </Button>
        )}
      </div>
      {zoom && img && <ImageLightbox src={img} alt={product.name} onClose={() => setZoom(false)} />}
    </div>
  );
}
